import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ChevronDown, ChevronUp, Star } from "lucide-react";
import api from "../lib/api";
import { ILE_DE_FRANCE, MAJOR_CITIES, REGIONS, DEPARTMENT_NAMES } from "../lib/regions";

export default function AllCities() {
  const [deptStats, setDeptStats] = useState({});  // {code: count}
  const [cityStats, setCityStats] = useState({});  // {slug: count}
  const [openRegions, setOpenRegions] = useState({});

  useEffect(() => {
    api.get("/companies/departments").then((r) => {
      const map = {};
      (r.data || []).forEach((d) => { map[d.code] = d.count; });
      setDeptStats(map);
    });
    api.get("/companies/cities").then((r) => {
      const map = {};
      (r.data || []).forEach((c) => { map[c.slug] = c.count; });
      setCityStats(map);
    });
    document.title = "Déménageur Pro — Toutes les régions et villes";
  }, []);

  const idfTotal = ILE_DE_FRANCE.reduce((s, d) => s + (deptStats[d.code] || 0), 0);
  const toggleRegion = (slug) => setOpenRegions((o) => ({ ...o, [slug]: !o[slug] }));

  return (
    <div className="animate-fade-up py-16 md:py-24">
      <div className="container mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="eyebrow mb-3">Couverture nationale</div>
          <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight text-[#2C3E50]">Annuaire par région</h1>
          <p className="text-[#6B7280] mt-4 max-w-2xl mx-auto">Trouvez les meilleurs déménageurs de votre département ou directement de votre ville.</p>
        </div>

        {/* ===== ILE-DE-FRANCE BLOCK ===== */}
        <div className="mb-14" data-testid="idf-block">
          <div className="bg-gradient-to-br from-[#1A2A3A] to-[#2C3E50] text-white rounded-3xl p-8 md:p-10 mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#2BD391]/15 rounded-full blur-3xl"></div>
            <div className="relative flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2BD391] text-white text-xs font-bold uppercase tracking-wider mb-4">
                  <Star className="w-3 h-3 fill-white" /> Région #1
                </div>
                <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-white mb-2" style={{ color: 'white' }}>Île-de-France</h2>
                <p className="text-white/80">Tous les déménageurs des 8 départements franciliens</p>
              </div>
              <div className="text-right">
                <div className="font-heading font-extrabold text-5xl text-[#2BD391]">{idfTotal.toLocaleString("fr-FR")}</div>
                <div className="text-xs uppercase tracking-widest text-white/70 mt-1">entreprises référencées</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ILE_DE_FRANCE.map((d) => (
              <Link
                key={d.code}
                to={`/demenageurs/departement/${d.code}`}
                data-testid={`idf-dept-${d.code}`}
                className="group bg-white border border-[#E5E7EB] hover:border-[#2BD391] rounded-2xl p-5 transition-all hover:shadow-[0_12px_32px_-8px_rgba(43,211,145,0.18)]"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-[#E8FBF3] group-hover:bg-[#2BD391] rounded-full flex items-center justify-center transition-colors">
                    <span className="font-heading font-extrabold text-[#1AA66C] group-hover:text-white">{d.code}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#2BD391] group-hover:translate-x-1 transition-all" />
                </div>
                <div className="font-heading font-bold text-[#2C3E50] group-hover:text-[#1AA66C]">{d.short}</div>
                <div className="text-xs text-[#6B7280] mt-1">
                  {deptStats[d.code] ? `${deptStats[d.code]} déménageurs` : "Découvrir"}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* ===== GRANDES VILLES ===== */}
        <div className="mb-14" data-testid="major-cities-block">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="eyebrow mb-2">Top France</div>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#2C3E50]">Grandes villes de France</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3">
            {MAJOR_CITIES.filter((c) => c.slug !== "paris").map((c) => (
              <Link
                key={c.slug}
                to={`/demenageurs/${c.slug}`}
                data-testid={`major-city-${c.slug}`}
                className="group flex items-center justify-between bg-[#F7F9FB] hover:bg-[#E8FBF3] border border-transparent hover:border-[#2BD391] rounded-2xl px-5 py-4 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center group-hover:bg-[#2BD391] transition-colors">
                    <MapPin className="w-4 h-4 text-[#2BD391] group-hover:text-white" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-[#2C3E50] group-hover:text-[#1AA66C]">{c.name}</div>
                    <div className="text-xs text-[#6B7280]">
                      {cityStats[c.slug] ? `${cityStats[c.slug]} déménageurs` : `Dépt. ${c.department}`}
                    </div>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#2BD391] group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>

        {/* ===== ALL REGIONS ===== */}
        <div data-testid="all-regions-block">
          <div className="mb-6">
            <div className="eyebrow mb-2">Annuaire complet</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#2C3E50]">Toutes les régions</h2>
          </div>
          <div className="space-y-3">
            {REGIONS.filter((r) => r.slug !== "ile-de-france").map((region) => {
              const total = region.departments.reduce((s, c) => s + (deptStats[c] || 0), 0);
              const isOpen = openRegions[region.slug];
              return (
                <div key={region.slug} className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid={`region-${region.slug}`}>
                  <button
                    onClick={() => toggleRegion(region.slug)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#F7F9FB] transition-colors"
                  >
                    <div className="flex items-center gap-4 text-left">
                      <div className="font-heading font-extrabold text-lg text-[#2C3E50]">{region.name}</div>
                      <div className="text-xs text-[#6B7280]">{region.departments.length} départements</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-[#1AA66C]">{total.toLocaleString("fr-FR")} déménageurs</span>
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 border-t border-[#F0F2F5] pt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                      {region.departments.map((code) => (
                        <Link
                          key={code}
                          to={`/demenageurs/departement/${code}`}
                          data-testid={`region-dept-${code}`}
                          className="group flex items-center justify-between bg-[#F7F9FB] hover:bg-[#E8FBF3] rounded-xl px-4 py-3 transition-all"
                        >
                          <div>
                            <div className="text-xs text-[#6B7280] font-mono">{code}</div>
                            <div className="font-bold text-sm text-[#2C3E50] group-hover:text-[#1AA66C]">{DEPARTMENT_NAMES[code] || code}</div>
                          </div>
                          <span className="text-xs font-bold text-[#1AA66C]">{deptStats[code] || 0}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
